import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Plus,
  Search,
  Edit,
  Mail,
  Users,
  UserCheck,
  UserX,
  Phone,
  Calendar,
  CreditCard,
  Briefcase,
  UserCircle2,
  LogOut,
  Flame,
} from "lucide-react";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  APL_LINK,
  action_update_res_staff,
  get_all_staff_captain,
  logout_single_staff,
  server_post_data,
} from "../ServiceConnection/serviceconnection";

export default function StaffManagement() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [staffData, setStaffData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [staffImageLink, setStaffImageLink] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  // Dialog states
  const [isToggleDialogOpen, setIsToggleDialogOpen] = useState(false);
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const [isFirebaseDialogOpen, setIsFirebaseDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [statusChangeMessage, setStatusChangeMessage] = useState("");
  const [firebaseMessage, setFirebaseMessage] = useState("");

  // ── Fetch ────────────────────────────────────────────────────────────────────
  const fetchStaffData = useCallback(async () => {
    setShowLoaderAdmin(true);
    setLoading(true);
    const fd = new FormData();
    fd.append("start_date", new Date("2025-01-26"));
    fd.append("end_date", new Date("2025-02-25"));
    fd.append("flag", "1");
    fd.append("call_id", "0");
    fd.append("search_option", "All");
    fd.append("search_data", "");

    try {
      const response = await server_post_data(get_all_staff_captain, fd);
      if (!response.data.error) {
        const designations = response.data.message.data_designation || [];
        const staff = (response.data.message.data_staff || []).map((s) => ({
          ...s,
          designation_name:
            designations
              .filter((d) => s.admin_designation === d.primary_id)
              .map((d) => d.designation_name)[0] || "N/A",
        }));
        setStaffData(staff);
        setFilteredData(staff);
        setStaffImageLink(APL_LINK + response.data.message.data_link_image);
      } else {
        setStaffData([]);
        setFilteredData([]);
      }
    } catch {
      toast({ title: "Error", description: "Failed to load staff data", variant: "destructive" });
    } finally {
      setShowLoaderAdmin(false);
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchStaffData(); }, [fetchStaffData]);

  // ── Search ───────────────────────────────────────────────────────────────────
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    if (!query.trim()) { setFilteredData(staffData); return; }
    const q = query.toLowerCase();
    setFilteredData(
      staffData.filter(
        (s) =>
          s.admin_name?.toLowerCase().includes(q) ||
          s.admin_email?.toLowerCase().includes(q) ||
          s.admin_mobile_no?.toString().toLowerCase().includes(q) ||
          s.designation_name?.toLowerCase().includes(q) ||
          s.personal_identity_no?.toString().toLowerCase().includes(q) ||
          s.admin_gender?.toLowerCase().includes(q),
      ),
    );
  }, [staffData]);

  // ── Allow Access toggle ──────────────────────────────────────────────────────
  const handleToggleClick = useCallback((staff) => {
    setSelectedStaff(staff);
    setStatusChangeMessage(
      staff.allow_access === "1"
        ? `Are you sure you want to deactivate ${staff.admin_name}?`
        : `Are you sure you want to activate ${staff.admin_name}?`,
    );
    setIsToggleDialogOpen(true);
  }, []);

  const handleConfirmToggle = useCallback(async () => {
    if (!selectedStaff) return;
    setShowLoaderAdmin(true);
    const newStatus = selectedStaff.allow_access === "1" ? "0" : "1";
    const fd = new FormData();
    fd.append("id_for_delete", selectedStaff.primary_id);
    fd.append("flag_for", "1");
    fd.append("for_status_final", newStatus);
    try {
      const response = await server_post_data(action_update_res_staff, fd);
      if (response.data.error) {
        toast({ title: "Error", description: response.data.message || "Failed to update status", variant: "destructive" });
      } else {
        toast({ title: "Success", description: `Staff ${newStatus === "1" ? "activated" : "deactivated"} successfully` });
        fetchStaffData();
      }
    } catch {
      toast({ title: "Error", description: "Network error. Please try again.", variant: "destructive" });
    } finally {
      setShowLoaderAdmin(false);
      setIsToggleDialogOpen(false);
      setSelectedStaff(null);
    }
  }, [selectedStaff, toast, fetchStaffData]);

  // ── Logout all devices ───────────────────────────────────────────────────────
  const handleLogoutClick = useCallback((staff) => {
    setSelectedStaff(staff);
    setIsLogoutDialogOpen(true);
  }, []);

  const handleConfirmLogout = useCallback(async () => {
    if (!selectedStaff) return;
    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("flag_restaurant", "1");
    fd.append("admin_id_for_logout", selectedStaff.primary_id);
    try {
      const response = await server_post_data(logout_single_staff, fd);
      if (response.data.error) {
        toast({ title: "Error", description: response.data.message || "Failed to logout staff", variant: "destructive" });
      } else {
        toast({ title: "Success", description: `${selectedStaff.admin_name} has been logged out from all devices` });
      }
    } catch {
      toast({ title: "Error", description: "Network error. Please try again.", variant: "destructive" });
    } finally {
      setShowLoaderAdmin(false);
      setIsLogoutDialogOpen(false);
      setSelectedStaff(null);
    }
  }, [selectedStaff, toast]);

  // ── Firebase access toggle ───────────────────────────────────────────────────
  const handleFirebaseClick = useCallback((staff) => {
    setSelectedStaff(staff);
    setFirebaseMessage(
      staff.firebase_access_status === 1
        ? `Remove Firebase notification access from ${staff.admin_name}?`
        : `Assign Firebase notification access to ${staff.admin_name}? This will remove it from the current holder.`,
    );
    setIsFirebaseDialogOpen(true);
  }, []);

  const handleConfirmFirebase = useCallback(async () => {
    if (!selectedStaff) return;
    setShowLoaderAdmin(true);
    const newStatus = selectedStaff.firebase_access_status === 1 ? "0" : "1";
    const fd = new FormData();
    fd.append("id_for_delete", selectedStaff.primary_id);
    fd.append("flag_for", "2");
    fd.append("for_status_final", newStatus);
    try {
      const response = await server_post_data(action_update_res_staff, fd);
      if (response.data.error) {
        toast({ title: "Error", description: response.data.message, variant: "destructive" });
      } else {
        toast({
          title: "Success",
          description: newStatus === "1"
            ? `Firebase access assigned to ${selectedStaff.admin_name}`
            : `Firebase access removed from ${selectedStaff.admin_name}`,
        });
        fetchStaffData();
      }
    } catch {
      toast({ title: "Error", description: "Network error. Please try again.", variant: "destructive" });
    } finally {
      setShowLoaderAdmin(false);
      setIsFirebaseDialogOpen(false);
      setSelectedStaff(null);
    }
  }, [selectedStaff, toast, fetchStaffData]);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const formatDate = (d) => {
    if (!d) return "N/A";
    return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const handleImageError = (e) => {
    e.target.style.display = "none";
    e.target.nextSibling.style.display = "flex";
  };

  const stats = {
    total: staffData.length,
    active: staffData.filter((s) => s.allow_access === "1").length,
    inactive: staffData.filter((s) => s.allow_access === "0").length,
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <MainLayout>
      <Header title="Staff Management" subtitle="Manage your restaurant staff and employees" />

      <div className="p-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon: Users, color: "primary", label: "Total Staff", value: stats.total },
            { icon: UserCheck, color: "success", label: "Active", value: stats.active },
            { icon: UserX, color: "destructive", label: "Inactive", value: stats.inactive },
          ].map(({ icon: Icon, color, label, value }) => (
            <div key={label} className="stat-card">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg bg-${color}/10 flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 text-${color}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{value}</p>
                  <p className="text-sm text-muted-foreground">{label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters & Actions */}
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="flex flex-1 gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, mobile, designation..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            {searchQuery && (
              <Button variant="outline" onClick={() => { setSearchQuery(""); setFilteredData(staffData); }}>
                Show All
              </Button>
            )}
          </div>
          <Button onClick={() => navigate("/add-Staff")} className="gap-2 gradient-primary text-primary-foreground hover:opacity-90">
            <Plus className="w-4 h-4" /> Add Staff
          </Button>
        </div>

        {/* Staff Cards */}
        <div className="space-y-4">
          {loading ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground"><p>Loading Staff...</p></CardContent></Card>
          ) : filteredData.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No staff members found</p>
              </CardContent>
            </Card>
          ) : (
            filteredData.map((staff) => (
              <Card key={staff.primary_id} className="overflow-hidden">
                <CardContent className="p-0">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-0">

                    {/* Personal Details */}
                    <div className="md:col-span-4 p-6 border-b md:border-b-0 md:border-r">
                      <div className="flex items-start gap-4">
                        <div className="relative">
                          <img
                            src={staffImageLink + staff.admin_image}
                            onError={handleImageError}
                            alt={staff.admin_name}
                            className="w-16 h-16 rounded-full object-cover"
                          />
                          <div style={{ display: "none" }} className="w-16 h-16 rounded-full bg-muted items-center justify-center">
                            <UserCircle2 className="w-10 h-10 text-muted-foreground" />
                          </div>
                          <div className={cn(
                            "absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white",
                            staff.allow_access === "1" ? "bg-success" : "bg-muted-foreground",
                          )} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className={cn("font-semibold text-base mb-1 truncate", staff.allow_access === "1" ? "text-foreground" : "text-muted-foreground")}>
                            {staff.admin_name}
                          </h3>
                          <div className="space-y-1 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              <span>DOB: {formatDate(staff.admin_dob_date)}</span>
                            </div>
                            <p>{staff.admin_gender}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Contact Details */}
                    <div className="md:col-span-3 p-6 border-b md:border-b-0 md:border-r">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-muted-foreground" />
                          <span className="text-foreground">{staff.admin_mobile_no}</span>
                        </div>
                        <div className="flex items-start gap-2 text-sm">
                          <Mail className="w-4 h-4 text-muted-foreground mt-0.5" />
                          <span className="text-foreground break-all">{staff.admin_email}</span>
                        </div>
                        <div
                          onClick={() => handleLogoutClick(staff)}
                          className="flex items-start gap-2 text-sm cursor-pointer hover:opacity-80 transition-opacity"
                        >
                          <LogOut className="w-4 h-4 text-destructive mt-0.5" />
                          <span className="text-destructive break-all font-medium">Logout of all devices</span>
                        </div>
                      </div>
                    </div>

                    {/* Employee Details */}
                    <div className="md:col-span-3 p-6 border-b md:border-b-0 md:border-r">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-muted-foreground" />
                          <Badge variant="outline" className="font-normal">{staff.designation_name}</Badge>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CreditCard className="w-4 h-4" />
                          <span className="text-foreground">ID: {staff.personal_identity_no}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          <span className="text-foreground">Joined: {formatDate(staff.admin_join_date)}</span>
                        </div>
                        {/* Firebase Access */}
                        <div
                          onClick={() => handleFirebaseClick(staff)}
                          className="flex items-center gap-2 text-sm cursor-pointer hover:opacity-80 transition-opacity"
                        >
                          <Flame className={cn("w-4 h-4", staff.firebase_access_status === 1 ? "text-orange-500" : "text-muted-foreground")} />
                          <span className={cn("font-medium", staff.firebase_access_status === 1 ? "text-orange-500" : "text-muted-foreground")}>
                            {staff.firebase_access_status === 1 ? "Firebase: Active" : "Firebase: Inactive"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="md:col-span-2 p-6 flex flex-col justify-center gap-3">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleToggleClick(staff)}
                          className={cn(
                            "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                            staff.allow_access === "1" ? "bg-success" : "bg-muted-foreground/30",
                          )}
                        >
                          <span className={cn(
                            "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                            staff.allow_access === "1" ? "translate-x-6" : "translate-x-1",
                          )} />
                        </button>
                        <span className="text-sm text-muted-foreground">
                          {staff.allow_access === "1" ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <Link to={`/edit-staff/${staff.primary_id}`}>
                        <Button variant="outline" size="sm" className="gap-2 w-full">
                          <Edit className="w-4 h-4" /> Edit Staff
                        </Button>
                      </Link>
                    </div>

                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* ── Dialogs ──────────────────────────────────────────────────────────── */}

        {/* Allow Access Toggle */}
        <AlertDialog open={isToggleDialogOpen} onOpenChange={setIsToggleDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Change Staff Status</AlertDialogTitle>
              <AlertDialogDescription>{statusChangeMessage}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmToggle}
                disabled={showLoaderAdmin}
                className={cn(
                  selectedStaff?.allow_access === "1"
                    ? "bg-destructive hover:bg-destructive/90"
                    : "gradient-primary text-primary-foreground hover:opacity-90",
                )}
              >
                {showLoaderAdmin ? "Updating..." : selectedStaff?.allow_access === "1" ? "Deactivate" : "Activate"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Logout All Devices */}
        <AlertDialog open={isLogoutDialogOpen} onOpenChange={setIsLogoutDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Logout from All Devices</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to logout {selectedStaff?.admin_name} from all devices? They will need to login again on all their devices.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmLogout}
                disabled={showLoaderAdmin}
                className="bg-destructive hover:bg-destructive/90"
              >
                {showLoaderAdmin ? "Processing..." : "Logout All Devices"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Firebase Access */}
        <AlertDialog open={isFirebaseDialogOpen} onOpenChange={setIsFirebaseDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Flame className="w-5 h-5 text-orange-500" />
                Firebase Notification Access
              </AlertDialogTitle>
              <AlertDialogDescription>{firebaseMessage}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmFirebase}
                disabled={showLoaderAdmin}
                className={cn(
                  selectedStaff?.firebase_access_status === 1
                    ? "bg-destructive hover:bg-destructive/90"
                    : "bg-orange-500 hover:bg-orange-600 text-white",
                )}
              >
                {showLoaderAdmin
                  ? "Processing..."
                  : selectedStaff?.firebase_access_status === 1
                    ? "Remove Access"
                    : "Assign Access"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

      </div>
    </MainLayout>
  );
}